import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { getAcademicYears, getSchoolUnits } from '../services/organization.api'
import {
  ALL_SCOPE,
  getActiveScope,
  scopeKey,
  setActiveScope,
  subscribeToScope,
  type ActiveScope,
} from '../services/scope'
import type { AcademicYear, SchoolUnit } from '../types/domain'
import { useAuth } from './AuthContext'


interface ScopeContextValue {
  /** Units the signed-in user may switch between. */
  availableUnits: SchoolUnit[]
  academicYears: AcademicYear[]
  /** Selected unit id, or `ALL` for foundation-wide. */
  activeUnitId: string
  /** Selected academic year id, or `ALL` when none is resolvable. */
  activeAcademicYearId: string
  activeUnit: SchoolUnit | null
  activeAcademicYear: AcademicYear | null
  /** The year flagged active by organization settings, regardless of selection. */
  currentAcademicYear: AcademicYear | null
  /** True when the user is looking at a year other than the current one. */
  isViewingPastOrFutureYear: boolean
  /** True when the user may see every unit (GLOBAL or FOUNDATION scope). */
  isGlobalUser: boolean
  setActiveUnitId: (unitId: string) => void
  setActiveAcademicYearId: (academicYearId: string) => void
  /** Changes whenever unit or year changes; use as a refetch dependency. */
  scopeKey: string
}

const ScopeContext = createContext<ScopeContextValue | null>(null)

export function ScopeProvider({ children }: { children: ReactNode }) {
  const { user, activePersona } = useAuth()
  const [schoolUnits, setSchoolUnits] = useState<SchoolUnit[]>([])
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([])
  const [scope, setScope] = useState<ActiveScope>(() => getActiveScope())

  useEffect(() => subscribeToScope(setScope), [])

  useEffect(() => {
    if (!user) return
    let cancelled = false

    const isExternalOrFamily =
      activePersona === 'PARENT' ||
      user.role === 'referral' ||
      user.role === 'REFERRAL' ||
      user.role === 'referral_partner' ||
      user.role === 'REFERRAL_PARTNER'

    if (isExternalOrFamily) {
      setSchoolUnits([])
      setAcademicYears([])
      return
    }

    // Tidak ada data cadangan: unit palsu di header membuat pengguna mengira
    // sedang melihat unit tertentu padahal permintaannya gagal.
    getSchoolUnits()
      .then((res) => {
        if (!cancelled) setSchoolUnits(res?.data ?? [])
      })
      .catch(() => {
        if (!cancelled) setSchoolUnits([])
      })

    getAcademicYears()
      .then((res) => {
        if (!cancelled) setAcademicYears(res?.data ?? [])
      })
      .catch(() => {
        if (!cancelled) setAcademicYears([])
      })

    return () => {
      cancelled = true
    }
  }, [user, activePersona])

  // Role orang tua selalu bercakupan GLOBAL karena keluarga bisa mendaftar ke
  // unit mana pun. Cakupan itu bukan hak staf: tanpa disaring, observer atau
  // panitia satu unit yang juga orang tua akan melihat "Semua Unit (Yayasan)".
  const staffAssignments = useMemo(
    () =>
      (user?.assignments ?? []).filter(
        (assignment) => assignment.roleCode !== 'SPMB_PARENT' && assignment.roleCode !== 'parent',
      ),
    [user],
  )

  const isGlobalUser = useMemo(() => {
    if (user?.role === 'super_admin') return true
    if (staffAssignments.length === 0) return true
    return staffAssignments.some(
      (assignment) => assignment.scopeType === 'GLOBAL' || assignment.scopeType === 'FOUNDATION',
    )
  }, [user, staffAssignments])

  const availableUnits = useMemo(() => {
    const activeSchoolUnits = schoolUnits.filter((unit) => unit.isActive !== false)
    const baseUnits = activeSchoolUnits.length > 0 ? activeSchoolUnits : schoolUnits
    if (isGlobalUser) return baseUnits
    const allowedUnitIds = new Set(
      staffAssignments.filter((a) => a.schoolUnitId).map((a) => a.schoolUnitId),
    )
    const filtered = baseUnits.filter((unit) => allowedUnitIds.has(unit.id))
    return filtered.length > 0 ? filtered : baseUnits
  }, [isGlobalUser, schoolUnits, staffAssignments])

  // A unit-scoped user must never sit on "Semua Unit"; fall back to their first unit.
  const activeUnitId = useMemo(() => {
    if (isGlobalUser) return scope.unitId
    if (availableUnits.some((unit) => unit.id === scope.unitId)) return scope.unitId
    return availableUnits[0]?.id ?? scope.unitId
  }, [isGlobalUser, availableUnits, scope.unitId])

  // Keep storage in step so `fetchWithAuth` sends the unit actually shown.
  useEffect(() => {
    if (activeUnitId !== scope.unitId) setActiveScope({ unitId: activeUnitId })
  }, [activeUnitId, scope.unitId])

  const sortedAcademicYears = useMemo(
    () => [...academicYears].sort((a, b) => b.name.localeCompare(a.name)),
    [academicYears],
  )

  const activeAcademicYears = useMemo(
    () => sortedAcademicYears.filter((year) => year.isActive),
    [sortedAcademicYears],
  )

  const currentAcademicYear = useMemo(
    () => activeAcademicYears[0] ?? sortedAcademicYears.find((year) => year.isActive) ?? null,
    [activeAcademicYears, sortedAcademicYears],
  )

  // Scope switcher should only present and default to active academic years.
  const effectiveAcademicYears = useMemo(
    () => (activeAcademicYears.length > 0 ? activeAcademicYears : sortedAcademicYears),
    [activeAcademicYears, sortedAcademicYears],
  )

  const activeAcademicYearId = useMemo(() => {
    if (effectiveAcademicYears.some((year) => year.id === scope.academicYearId)) {
      return scope.academicYearId
    }
    return currentAcademicYear?.id ?? effectiveAcademicYears[0]?.id ?? ALL_SCOPE
  }, [effectiveAcademicYears, scope.academicYearId, currentAcademicYear])

  useEffect(() => {
    if (activeAcademicYearId !== ALL_SCOPE && activeAcademicYearId !== scope.academicYearId) {
      setActiveScope({ academicYearId: activeAcademicYearId })
    }
  }, [activeAcademicYearId, scope.academicYearId])

  const setActiveUnitId = useCallback((unitId: string) => {
    setActiveScope({ unitId })
  }, [])

  const setActiveAcademicYearId = useCallback((academicYearId: string) => {
    setActiveScope({ academicYearId })
  }, [])

  const activeUnit = useMemo(
    () => availableUnits.find((unit) => unit.id === activeUnitId) ?? null,
    [availableUnits, activeUnitId],
  )

  const activeAcademicYear = useMemo(
    () => effectiveAcademicYears.find((year) => year.id === activeAcademicYearId) ?? null,
    [effectiveAcademicYears, activeAcademicYearId],
  )

  const value = useMemo<ScopeContextValue>(
    () => ({
      availableUnits,
      academicYears: effectiveAcademicYears,
      activeUnitId,
      activeAcademicYearId,
      activeUnit,
      activeAcademicYear,
      currentAcademicYear,
      isViewingPastOrFutureYear: Boolean(
        currentAcademicYear && activeAcademicYearId !== currentAcademicYear.id,
      ),
      isGlobalUser,
      setActiveUnitId,
      setActiveAcademicYearId,
      scopeKey: scopeKey({ unitId: activeUnitId, academicYearId: activeAcademicYearId }),
    }),
    [
      availableUnits,
      effectiveAcademicYears,
      activeUnitId,
      activeAcademicYearId,
      activeUnit,
      activeAcademicYear,
      currentAcademicYear,
      isGlobalUser,
      setActiveUnitId,
      setActiveAcademicYearId,
    ],
  )

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
}

// oxlint-disable-next-line react/only-export-components
export const useActiveScope = (): ScopeContextValue => {
  const context = useContext(ScopeContext)
  if (!context) {
    throw new Error('useActiveScope harus dipakai di dalam ScopeProvider.')
  }
  return context
}
